const TICK_MS = 100
const SMOOTHING_MS = 600
const PHASES = {
  initializing: { start: 0, end: 5, duration: 1500 },
  uploading: { start: 5, end: 70 },
  completing: { start: 70, end: 90, duration: 8000 },
  covering: { start: 90, end: 95, duration: 3000 },
  importing: { start: 95, end: 99, duration: 5000 }
}

function supportsSmoothProgress(task) {
  const match = String(task.fileName || '').trim().toLowerCase().match(/\.([^.]+)$/)
  const extension = match ? match[1] : ''
  return (task.folderType === 'paper' && extension === 'pdf')
    || (task.folderType === 'learning' && ['pdf', 'epub', 'azw', 'azw3', 'mobi'].includes(extension))
}

// Only these imports use estimated progress: byte transfer is followed by hashing,
// page counting, cover generation/upload and the final import request.
export function createCloudImportProgress(task, onValue) {
  if (!task || !supportsSmoothProgress(task)) {
    return null
  }

  const attempt = task.abortController
  let phase = PHASES.initializing
  let phaseStartedAt = Date.now()
  let lastTickAt = phaseStartedAt
  let value = 0
  let displayed = 0
  let transferFraction = 0
  let timer = null
  let stopped = false
  let completed = false
  let finishing = false
  let finishStartValue = 0
  let finishElapsed = 0
  let finishPromise = null
  let resolveFinish = null

  function isActive() {
    return !task.cancelled && (task.state === 'running' || (finishing && task.state === 'done'))
      && task.abortController === attempt
      && !(attempt && attempt.signal && attempt.signal.aborted)
  }

  function stop() {
    stopped = true
    clearInterval(timer)
    timer = null
    if (resolveFinish) {
      resolveFinish(completed)
      resolveFinish = null
    }
  }

  function publish() {
    // Keep visible movement during slower processing stages instead of holding
    // the same whole-number percentage until the next network response.
    const next = completed ? 100 : Math.floor(value * 10) / 10
    if (next !== displayed) {
      displayed = next
      onValue(next)
    }
  }

  function getTarget(now) {
    if (phase === PHASES.uploading) {
      return phase.start + (phase.end - phase.start) * transferFraction
    }
    const elapsed = Math.max(0, now - phaseStartedAt)
    // Move linearly through most of each processing stage, retaining headroom
    // when the network/server takes longer than expected. Never imply success.
    const tail = Math.max(0, elapsed - phase.duration)
    const fraction = elapsed < phase.duration
      ? 0.8 * elapsed / phase.duration
      : 0.8 + 0.2 * tail / (tail + phase.duration)
    return phase.start + (phase.end - phase.start) * fraction
  }

  function tick() {
    if (stopped || !isActive()) {
      stop()
      return
    }
    const now = Date.now()
    // Resuming a backgrounded mobile tab must not cause a large visual jump.
    const elapsed = Math.max(0, Math.min(now - lastTickAt, TICK_MS * 2))
    lastTickAt = now
    if (finishing) {
      finishElapsed = Math.min(SMOOTHING_MS, finishElapsed + elapsed)
      value = finishStartValue + (100 - finishStartValue) * finishElapsed / SMOOTHING_MS
      if (value >= 100) {
        completed = true
        publish()
        stop()
        return
      }
    } else {
      const target = Math.max(value, getTarget(now))
      // A burst of XHR events or a phase change only changes the destination;
      // the displayed progress travels at at most one percent per 100ms.
      value = Math.min(target, value + elapsed / TICK_MS)
    }
    publish()
  }

  timer = setInterval(tick, TICK_MS)

  return {
    onPhase(name) {
      const next = PHASES[name]
      if (!stopped && !finishing && next && next.start > phase.start) {
        phase = next
        phaseStartedAt = Date.now()
      }
    },
    onProgress(progress) {
      const total = Number(progress && progress.total) || Number(task.fileSize) || 0
      const loaded = Math.max(0, Number(progress && progress.loaded) || 0)
      if (!stopped && !finishing && total > 0) {
        transferFraction = Math.max(transferFraction, Math.min(1, loaded / total))
      }
    },
    finish() {
      if (stopped || !isActive()) {
        stop()
        return Promise.resolve(completed)
      }
      if (!finishPromise) {
        finishing = true
        finishStartValue = value
        finishElapsed = 0
        finishPromise = new Promise(resolve => { resolveFinish = resolve })
      }
      return finishPromise
    },
    stop
  }
}
